import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { History, Megaphone, Plus, Trash2 } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  AUDIENCE_LABELS,
  createAnnouncement,
  deleteAnnouncement,
  fetchAllAnnouncements,
  updateAnnouncement,
  fetchAnnouncementHistory,
  formatHistoryValue,
  FIELD_LABELS,
  type Audience,
} from "@/lib/announcements";
import { useQuery as useHistoryQuery } from "@tanstack/react-query";

function AnnouncementHistory({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const { data: entries = [], isLoading } = useHistoryQuery({
    queryKey: ["announcement-history", id],
    queryFn: () => fetchAnnouncementHistory(id),
    enabled: open,
  });

  return (
    <div className="mt-2 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <History className="h-3 w-3" /> {open ? "Hide edit history" : "View edit history"}
      </button>
      {open ? (
        isLoading ? (
          <p className="mt-2 text-[11px] text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="mt-2 text-[11px] text-muted-foreground">
            No edits since this announcement was created.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {entries.map((e) => (
              <li key={e.id} className="rounded-md bg-muted/50 p-2">
                <p className="text-[11px] font-medium text-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(e.changes).map(([field, diff]) => (
                    <li key={field} className="text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {FIELD_LABELS[field] ?? field}:
                      </span>{" "}
                      {formatHistoryValue(field, diff.from)} → {formatHistoryValue(field, diff.to)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/super-admin/announcements")({
  head: () => ({
    meta: [
      { title: "Announcements · Landoo Super Admin" },
      {
        name: "description",
        content:
          "Create and schedule platform announcements for all users, customers, partners or admins.",
      },
      { property: "og:title", content: "Announcements · Landoo Super Admin" },
      {
        property: "og:description",
        content: "Publish scheduled announcements to Landoo audiences.",
      },
    ],
  }),
  component: AnnouncementsPage,
});

const input =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-xs outline-none focus:border-brand";

const toLocalInput = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");

function AnnouncementsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["announcements", "all"],
    queryFn: fetchAllAnnouncements,
  });

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState<Audience>("all");
  const [start, setStart] = useState(new Date().toISOString().slice(0, 16));
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["announcements"] });

  const submit = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Title and message are required");
      return;
    }
    if (end && new Date(end) <= new Date(start)) {
      toast.error("End date must be after the start date");
      return;
    }
    setBusy(true);
    try {
      await createAnnouncement(
        {
          title: title.trim(),
          message: message.trim(),
          audience,
          start_date: new Date(start).toISOString(),
          end_date: end ? new Date(end).toISOString() : null,
          active: true,
        },
        user!.id,
      );
      setTitle("");
      setMessage("");
      setEnd("");
      refresh();
      toast.success("Announcement published");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not create announcement");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id: string, active: boolean) => {
    try {
      await updateAnnouncement(id, { active });
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update");
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteAnnouncement(id);
      refresh();
      toast.success("Announcement deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete");
    }
  };

  return (
    <DashboardShell title="Announcements" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_1fr]">
        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Megaphone className="h-4 w-4 text-primary" /> New announcement
          </h2>
          <div className="mt-3 space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Title
              </span>
              <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Message
              </span>
              <textarea
                rows={4}
                className={input}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Audience
              </span>
              <select
                className={input}
                value={audience}
                onChange={(e) => setAudience(e.target.value as Audience)}
              >
                {(Object.keys(AUDIENCE_LABELS) as Audience[]).map((a) => (
                  <option key={a} value={a}>
                    {AUDIENCE_LABELS[a]}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                  Start date
                </span>
                <input
                  type="datetime-local"
                  className={input}
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-muted-foreground">
                  End date (optional)
                </span>
                <input
                  type="datetime-local"
                  className={input}
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </label>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={submit}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> Publish announcement
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">All announcements</h2>
          {isLoading ? (
            <p className="mt-3 text-xs text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="mt-3 text-xs text-muted-foreground">No announcements yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {rows.map((a) => (
                <li key={a.id} className="rounded-lg border border-border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{a.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{a.message}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {AUDIENCE_LABELS[a.audience]} · {new Date(a.start_date).toLocaleString()} →{" "}
                        {a.end_date ? new Date(a.end_date).toLocaleString() : "No end"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggle(a.id, !a.active)}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          a.active
                            ? "bg-emerald-500/10 text-emerald-600"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {a.active ? "Active" : "Inactive"}
                      </button>
                      <button
                        type="button"
                        aria-label="Delete announcement"
                        onClick={() => remove(a.id)}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <input type="hidden" value={toLocalInput(a.end_date)} readOnly />
                  <AnnouncementHistory id={a.id} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}
